import { useState } from 'react';

import Button from '@liquidweb-dev/undercurrent/Button';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import PlusIcon from '@liquidweb-dev/undercurrent/icons/lucide/Plus';
import XIcon from '@liquidweb-dev/undercurrent/icons/lucide/X';
import Popover, {
	type PopoverProps,
} from '@liquidweb-dev/undercurrent/Popover';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import toast from '@liquidweb-dev/undercurrent/toast';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import LWForm, { type FormSubmitHandler } from 'components/LWForm/LWForm';
import HookedSubmit from 'components/standard/Form/HookedSubmit';
import HookedTextField from 'components/standard/Form/HookedTextField';
import useSecureDataShareTicketLinkCreate from 'modules/queries/secureDataShare/ticketlink/useCreate';
import useSecureDataShareTicketLinkRemove from 'modules/queries/secureDataShare/ticketlink/useRemove';
import { TransitionGroup } from 'react-transition-group';

export interface PopoverSupportCaseProps extends Omit<PopoverProps, 'onClose'> {
	datashareId: string | number;
	ticketNumbers: {
		case_id: string;
		ticket_number: number | string;
	}[];
	onClose: () => void;
}

const PopoverSupportCase = ({
	datashareId,
	ticketNumbers,
	onClose,
	...popoverProps
}: PopoverSupportCaseProps) => {
	const [formValues, setFormValues] = useState({
		ticket: '',
	});
	const [formErrors, setFormErrors] = useState({});

	const { mutate: createTicketLink, isLoading: isCreatingTicketLink } =
		useSecureDataShareTicketLinkCreate();
	const {
		mutate: removeTicketLink,
		isLoading: isRemovingTicketLink,
		variables: removeTicketLinkVariables,
	} = useSecureDataShareTicketLinkRemove();

	const handleSubmit: FormSubmitHandler<typeof formValues> = ({ ticket }) => {
		createTicketLink(
			{ datashare_id: datashareId.toString(), ticket },
			{
				onSuccess: () => {
					toast.success(`Added case ${ticket}`);
					setFormValues({ ticket: '' });
				},
				onError: () => toast.danger(`Failed to add case ${ticket}`),
			},
		);
	};

	const handleRemoveTicket = (ticket: string) => {
		removeTicketLink(
			{ datashare_id: datashareId.toString(), ticket },
			{
				onSuccess: () => toast.success(`Removed case ${ticket}`),
				onError: () => toast.danger(`Failed to remove case ${ticket}`),
			},
		);
	};

	const handleClose = () => {
		setFormValues({ ticket: '' });
		setFormErrors({});
		onClose();
	};

	const hasCases = ticketNumbers.length > 0;
	const title = hasCases ? 'Edit support cases' : 'Add a support case';
	const description = hasCases
		? 'Add or remove support cases from this note'
		: 'Attach related support cases to this note';

	return (
		<Popover
			onClose={handleClose}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'right',
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			{...popoverProps}
		>
			<Stack>
				<Stack sx={{ gap: 100, marginBottom: hasCases ? 100 : 200 }}>
					<Typography variant="label-xl">{title}</Typography>
					<Typography
						variant="body-sm"
						sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
					>
						{description}
					</Typography>
				</Stack>

				<TransitionGroup>
					{ticketNumbers.map(({ ticket_number: ticket }) => (
						<Collapse key={ticket}>
							<Stack
								sx={{
									padding: 100,
									gap: 100,
									flexDirection: 'row',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}
							>
								<Typography variant="label-xl">{ticket}</Typography>
								<IconButton
									variant="barebones"
									size="small"
									onClick={() => handleRemoveTicket(ticket.toString())}
									loading={
										isRemovingTicketLink &&
										removeTicketLinkVariables?.ticket === ticket.toString()
									}
								>
									<XIcon />
								</IconButton>
							</Stack>
						</Collapse>
					))}
				</TransitionGroup>

				<LWForm
					values={formValues}
					onValuesChange={setFormValues}
					errors={formErrors}
					onErrorsChange={setFormErrors}
					onSubmit={handleSubmit}
				>
					<Stack sx={{ gap: 100, marginTop: hasCases ? 100 : 0 }}>
						<HookedTextField name="ticket" placeholder="Case number" />

						<Stack sx={{ gap: 100, flexDirection: 'row' }}>
							<Button
								variant="secondary"
								size="large"
								onClick={handleClose}
								sx={{ flex: 1 }}
							>
								Cancel
							</Button>
							<HookedSubmit
								size="large"
								color="highlight"
								startIcon={<PlusIcon />}
								loading={isCreatingTicketLink}
								disabled={formValues.ticket === ''}
								sx={{ flex: 1 }}
							>
								Add
							</HookedSubmit>
						</Stack>
					</Stack>
				</LWForm>
			</Stack>
		</Popover>
	);
};

export default PopoverSupportCase;
